import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, ApiError, qs } from "@/lib/api";
import { formatCents, formatDate, formatDateTime } from "@/lib/format";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { MoneyApprovalsSection } from "./MoneyApprovals";

/** Billing decisions that wait for Brandon (crm-billing: approvals_list / approvals_get / approvals_decide). */

interface Option {
  key: string;
  label: string;
}

interface Invoice {
  id: number;
  number: string;
  description: string | null;
  amount_due_cents: number;
  amount_paid_cents: number;
  due_date: string | null;
  status: string;
}

interface Approval {
  id: number;
  kind: string;
  status: "pending" | "approved" | "rejected" | "expired" | "cancelled";
  contact_id: number | null;
  summary: string;
  options: Option[] | null;
  context: Record<string, unknown>;
  decision: string | null;
  requested_at: string;
  requested_by: string;
  decided_at: string | null;
  decided_by: string | null;
  invoices?: Invoice[];
}

interface Preview {
  what_happens?: string;
  invoices?: string[];
  card?: string;
  question?: string;
}

interface DecideResult {
  message: string;
  charges: { invoice_number: string; status: string; amount_cents: number; message: string }[];
  voided_invoices: string[];
}

const kindLabel: Record<string, string> = {
  overdue_invoices_new_card: "Overdue invoices on a new card",
  duplicate_payment: "Paid twice",
};

type Filter = "pending" | "all";

export function ApprovalsPage() {
  const [filter, setFilter] = useState<Filter>("pending");
  const q = useQuery({
    queryKey: ["approvals", filter],
    queryFn: () => api<{ approvals: Approval[] }>("/api/v1/approvals" + qs({ status: filter, limit: 100 })),
  });
  const items = q.data?.approvals ?? [];
  return (
    <div className="mx-auto max-w-[1000px]">
      <PageHeader
        title="Approvals"
        sub={filter === "pending" && q.data ? `${items.length} waiting` : undefined}
        actions={
          <div role="radiogroup" aria-label="Show" className="flex gap-1">
            {(
              [
                ["pending", "Waiting"],
                ["all", "All"],
              ] as const
            ).map(([v, label]) => (
              <Button key={v} size="sm" role="radio" aria-checked={filter === v} variant={filter === v ? "default" : "outline"} onClick={() => setFilter(v)}>
                {label}
              </Button>
            ))}
          </div>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Billing never decides these on its own. When a member with two or more overdue invoices adds a new card, nothing is charged until you choose. Every choice
          shows exactly what will happen before anything moves.
        </p>
      </PageHeader>
      <MoneyApprovalsSection filter={filter} />
      <h2 className="mb-2 text-sm font-semibold">Billing decisions</h2>
      {q.isLoading && <Skeleton className="h-48" />}
      {q.isError && (
        <Card>
          <Empty title="Couldn't load approvals">{(q.error as Error).message}</Empty>
        </Card>
      )}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <Card>
          <Empty title={filter === "pending" ? "Nothing waiting for you." : "No approvals yet."} />
        </Card>
      )}
      <div className="space-y-3">
        {items.map((a) => (
          <ApprovalCard key={a.id} a={a} />
        ))}
      </div>
    </div>
  );
}

function ApprovalCard({ a }: { a: Approval }) {
  const detail = useQuery({
    queryKey: ["approval", a.id],
    queryFn: () => api<Approval>(`/api/v1/approvals/${a.id}`),
    enabled: a.status === "pending",
  });
  const [choice, setChoice] = useState<Option | null>(null);
  const invoices = detail.data?.invoices ?? [];
  const options: Option[] = [...(a.options ?? [])];
  if (a.kind === "overdue_invoices_new_card") options.push({ key: "dismiss", label: "Leave them unpaid for now" });
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm">
        <Badge variant={a.status === "pending" ? "warning" : a.status === "approved" ? "success" : "muted"}>{kindLabel[a.kind] ?? a.kind}</Badge>
        {a.contact_id && (
          <Link to="/contacts/$contactId" params={{ contactId: String(a.contact_id) }} className="text-sm underline-offset-4 hover:underline">
            Contact #{a.contact_id}
          </Link>
        )}
        <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
          {a.status === "pending" ? `asked ${formatDateTime(a.requested_at)}` : `${a.decision ?? a.status} ${formatDateTime(a.decided_at)} by ${a.decided_by ?? "?"}`}
        </span>
      </div>
      <div className="space-y-3 p-4">
        <p className="text-sm">{a.summary}</p>
        {invoices.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[var(--color-muted-foreground)]">
                <th className="py-1 font-medium">Invoice</th>
                <th className="py-1 font-medium">For</th>
                <th className="py-1 font-medium">Due</th>
                <th className="py-1 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t">
                  <td className="num py-1.5">{inv.number}</td>
                  <td className="py-1.5">{inv.description}</td>
                  <td className="py-1.5">{formatDate(inv.due_date)}</td>
                  <td className="num py-1.5 text-right">{formatCents(inv.amount_due_cents - inv.amount_paid_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {a.status === "pending" && (
          <div className="flex flex-wrap gap-2">
            {options.map((o) => (
              <Button key={o.key} size="sm" variant={o.key === "dismiss" || o.key === "keep" ? "outline" : "default"} onClick={() => setChoice(o)}>
                {o.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      {choice && <DecideDialog a={a} choice={choice} onClose={() => setChoice(null)} />}
    </Card>
  );
}

/** Step 1 asks the server what would happen (confirm_required + preview); step 2 confirms. */
function DecideDialog({ a, choice, onClose }: { a: Approval; choice: Option; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState("");
  const preview = useQuery({
    queryKey: ["approval-preview", a.id, choice.key],
    retry: false,
    queryFn: async () => {
      try {
        await api(`/api/v1/approvals/${a.id}/decide`, { json: { decision: choice.key } });
        return {} as Preview;
      } catch (e) {
        if (e instanceof ApiError && e.code === "confirm_required") return (e.details ?? {}) as Preview;
        throw e;
      }
    },
  });
  const decide = useMutation({
    mutationFn: () => api<DecideResult>(`/api/v1/approvals/${a.id}/decide`, { json: { decision: choice.key, note: note || undefined, confirm: true } }),
    onSuccess: (r) => {
      toast(r.message || "Done");
      void qc.invalidateQueries({ queryKey: ["approvals"] });
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const p = preview.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{choice.label}</DialogTitle>
        <DialogDescription>Check what will happen, then confirm. Nothing has moved yet.</DialogDescription>
        {preview.isLoading && <Skeleton className="h-24" />}
        {preview.isError && <p className="text-sm text-[var(--color-danger-fg)]">{(preview.error as Error).message}</p>}
        {p && (
          <div className="space-y-3 text-sm">
            {p.what_happens && <p className="font-medium">{p.what_happens}</p>}
            {p.card && (
              <p>
                Card: <span className="num">{p.card}</span>
              </p>
            )}
            {p.invoices && p.invoices.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5">
                {p.invoices.map((s) => (
                  <li key={s} className="num">
                    {s}
                  </li>
                ))}
              </ul>
            )}
            <label className="block">
              <span className="mb-1 block text-xs text-[var(--color-muted-foreground)]">Note (optional, saved with the decision)</span>
              <input
                className="h-9 w-full rounded-md border bg-[var(--color-card)] px-2 text-sm"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={300}
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => decide.mutate()} disabled={decide.isPending}>
                {decide.isPending ? "Working…" : "Confirm"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
